import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Grid,
  Avatar,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useAccount } from '../../contexts/AccountContext';
import {
  Group as GroupIcon,
  Person as PersonIcon,
  AccountTree as AccountTreeIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  AssignmentInd as AssignmentIndIcon,
  Label as LabelIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const TeamDetail = ({
  team,
  teams = [],
  users = [],
  onClose,
  onEdit,
  onAddMember,
  onRemoveMember,
  userRole = 'AG', // Default to Agent (view-only)
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editableTeam, setEditableTeam] = useState({ ...team, tags: team.tags || [] });
  const [errors, setErrors] = useState({});
  const { accountId } = useAccount();

  // Get team members
  const teamMembers = users.filter(user => user.team_id === team.id);
  
  // Get team lead - check both lead and manager fields for backwards compatibility
  const teamLead = users.find(user => user.id === (team.lead || team.manager));
  
  // Get parent team
  const parentTeam = teams.find(t => t.id === team.parent_team);
  
  // Get child teams
  const childTeams = teams.filter(t => t.parent_team === team.id);
  
  // Get available users who don't belong to any team and are not Admins
  const availableUsers = users.filter(user => !user.team_id && user.role !== 'AD');
  
  // Calculate total agent count including sub-teams
  const calculateTotalAgentCount = (teamId) => {
    // Direct members
    const directMembers = users.filter(user => user.team_id === teamId);
    
    // Find all child teams recursively
    const findAllChildTeams = (parentId) => {
      const children = teams.filter(t => t.parent_team === parentId);
      let allChildren = [...children];
      
      children.forEach(child => {
        const grandchildren = findAllChildTeams(child.id);
        allChildren = [...allChildren, ...grandchildren];
      });
      
      return allChildren;
    };
    
    // Get all sub-teams
    const allSubTeams = findAllChildTeams(teamId);
    
    // Count all members in sub-teams
    const subTeamMembers = users.filter(user => 
      allSubTeams.some(subTeam => user.team_id === subTeam.id)
    );
    
    return {
      directCount: directMembers.length,
      totalCount: directMembers.length + subTeamMembers.length,
      subTeamsCount: allSubTeams.length
    };
  };
  
  // Get member counts
  const memberCounts = calculateTotalAgentCount(team.id);
  
  // Determine user permissions based on role
  const userPermissions = {
    canEdit: userRole === 'AD', // Only Admin can edit teams
    canManageMembers: userRole === 'AD' || userRole === 'MG', // Admin and Managers can manage team members
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleEditOpen = () => {
    // Process tags that might contain comma-separated values
    let processedTags = [];
    if (team.tags && Array.isArray(team.tags)) {
      team.tags.forEach(tag => {
        if (tag.includes(',')) {
          // Split comma-separated tags and add each individually
          tag.split(',').forEach(subTag => {
            processedTags.push(subTag.trim());
          });
        } else {
          processedTags.push(tag);
        }
      });
    }
    
    setEditableTeam({ 
      ...team,
      tags: processedTags
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
  };

  const handleAddMemberOpen = () => {
    setSelectedUser(null);
    setAddMemberDialogOpen(true);
  };

  const handleAddMemberClose = () => {
    setAddMemberDialogOpen(false);
  };

  const handleEditSubmit = () => {
    // Validate
    const newErrors = {};
    
    if (!editableTeam.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (
      teams.some(
        t => t.id !== team.id && 
        t.name.toLowerCase() === editableTeam.name.toLowerCase()
      )
    ) {
      newErrors.name = 'Team name already exists';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Call the edit function
    onEdit(team.id, {
      name: editableTeam.name,
      parent_team: editableTeam.parent_team,
      lead: editableTeam.lead,
      tags: editableTeam.tags,
    });
    
    handleEditClose();
  };

  const handleAddMemberSubmit = () => {
    if (selectedUser) {
      onAddMember(team.id, selectedUser.id);
      handleAddMemberClose();
    }
  };

  const handleRemoveMember = (userId) => {
    onRemoveMember(accountId, team.id, userId);
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={onClose}
        >
          Back to Teams
        </Button>
        
        {userPermissions.canEdit && (
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<EditIcon />}
            onClick={handleEditOpen}
          >
            Edit Team
          </Button>
        )}
      </Box>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
            <GroupIcon />
          </Avatar>
          <Box>
            <Typography variant="h5">{team.name}</Typography>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          {/* Team Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Team Details
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Created:
              </Typography>
              <Typography variant="body1">
                {new Date(team.created_at).toLocaleDateString()}
              </Typography>
            </Box>
            
            {team.tags && Array.isArray(team.tags) && team.tags.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Tags:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {team.tags.map((tag, index) => (
                    tag.includes(',') ?
                      tag.split(',').map((subTag, subIndex) => (
                        <Chip
                          key={`${index}-${subIndex}`}
                          label={subTag.trim()}
                          size="small"
                          icon={<LabelIcon fontSize="small" />}
                        />
                      ))
                    :
                    <Chip
                      key={index}
                      label={tag}
                      size="small"
                      icon={<LabelIcon fontSize="small" />}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Team Size:
              </Typography>
              <Typography variant="body1">
                {teamMembers.length} direct members
              </Typography>
              {memberCounts.totalCount > memberCounts.directCount && (
                <Typography variant="body1">
                  {memberCounts.totalCount} total members (including {memberCounts.subTeamsCount} sub-teams)
                </Typography>
              )}
            </Box>
          </Grid>
          
          {/* Team Lead */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              Team Lead
            </Typography>
            
            {teamLead ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="body1">
                    {teamLead.name}
                    {teamLead.designation && <span style={{ fontSize: '0.9em', opacity: 0.8, marginLeft: '8px' }}>
                      ({teamLead.designation})
                    </span>}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {teamLead.role === 'MG' ? 'Manager' : 'Admin'} • {teamLead.email}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No team lead assigned
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Team Members" />
            {childTeams.length > 0 && <Tab label={`Sub-Teams (${childTeams.length})`} />}
          </Tabs>
        </Box>
        
        {/* Team Members Tab */}
        {tabValue === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Members ({teamMembers.length})</Typography>
              {userPermissions.canManageMembers && (
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={handleAddMemberOpen}
                  size="small"
                >
                  Add Member
                </Button>
              )}
            </Box>
            
            {teamMembers.length > 0 ? (
              <List>
                {teamMembers.map(member => (
                  <ListItem key={member.id} divider>
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <span>
                          {member.name} 
                          {member.designation && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({member.designation})
                          </Typography>}
                        </span>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span">
                            {member.email}
                          </Typography>
                          <br />
                          <Chip 
                            label={
                              member.role === 'AG' ? 'Agent' : 
                              member.role === 'MG' ? 'Manager' : 'Admin'
                            }
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                          {member.id === team.lead && (
                            <Chip 
                              label="Team Lead" 
                              color="primary" 
                              size="small" 
                              sx={{ ml: 1, mt: 0.5 }}
                            />
                          )}
                        </Box>
                      }
                    />
                    {userPermissions.canManageMembers && member.id !== team.lead && (
                      <ListItemSecondaryAction>
                        <Tooltip title="Remove from team">
                          <IconButton 
                            edge="end" 
                            onClick={() => handleRemoveMember(member.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  This team has no members yet.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Sub Teams Tab */}
        {tabValue === 1 && childTeams.length > 0 && (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">Sub Teams ({childTeams.length})</Typography>
            </Box>
            
            <List>
              {childTeams.map(childTeam => {
                const childTeamMembers = users.filter(user => user.team_id === childTeam.id);
                const childTeamLead = users.find(user => user.id === (childTeam.lead || childTeam.manager));
                
                return (
                  <ListItem 
                    key={childTeam.id} 
                    divider
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                    onClick={() => {
                      // Find the child team and pass it to the parent component to display
                      const selectedTeam = teams.find(t => t.id === childTeam.id);
                      if (selectedTeam) {
                        onClose();
                        // Use a small delay to ensure proper sequencing
                        setTimeout(() => {
                          // Use a custom event to communicate with parent component
                          const teamEvent = new CustomEvent('selectTeam', { 
                            detail: { team: selectedTeam }
                          });
                          window.dispatchEvent(teamEvent);
                        }, 50);
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <GroupIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={childTeam.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span">
                            {childTeamMembers.length} members
                          </Typography>
                          <br />
                          {childTeamLead && (
                            <Typography variant="body2" component="span">
                              Lead: {childTeamLead.name}
                              {childTeamLead.designation && <span style={{ fontSize: '0.9em', opacity: 0.8, marginLeft: '4px' }}>
                                ({childTeamLead.designation})
                              </span>}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {userPermissions.canEdit && (
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                                  // Open the child team directly for editing
                            const selectedTeam = teams.find(t => t.id === childTeam.id);
                            if (selectedTeam) {
                              // Using the same pattern as the parent component
                              // This way we stay in the Teams section
                              onClose();
                              setTimeout(() => {
                                // This will call handleTeamSelect in the parent Teams component
                                const teamEvent = new CustomEvent('selectTeam', { 
                                  detail: { team: selectedTeam }
                                });
                                window.dispatchEvent(teamEvent);
                              }, 50);
                            }
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </Paper>
      
      {/* Edit Team Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Team
          <IconButton
            onClick={handleEditClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Team Name"
              fullWidth
              value={editableTeam.name || ''}
              onChange={(e) => setEditableTeam({ ...editableTeam, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
            />
            
            <FormControl fullWidth>
              <InputLabel>Parent Team</InputLabel>
              <Select
                value={editableTeam.parent_team || ''}
                label="Parent Team"
                onChange={(e) => setEditableTeam({ ...editableTeam, parent_team: e.target.value })}
              >
                <MenuItem value="">
                  <em>None (Top-level team)</em>
                </MenuItem>
                {teams
                  .filter(t => {
                    // Exclude the current team
                    if (t.id === team.id) return false;
                    
                    // Check if t is a sub-team (direct or indirect) of the current team
                    // to prevent circular dependencies
                    const isSubTeam = (parentId, checkId) => {
                      const children = teams.filter(child => child.parent_team === parentId);
                      if (children.some(child => child.id === checkId)) return true;
                      return children.some(child => isSubTeam(child.id, checkId));
                    };
                    
                    return !isSubTeam(team.id, t.id);
                  })
                  .map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Team Lead</InputLabel>
              <Select
                value={editableTeam.lead || ''}
                label="Team Lead"
                onChange={(e) => setEditableTeam({ ...editableTeam, lead: e.target.value })}
              >
                <MenuItem value="">
                  <em>No team lead</em>
                </MenuItem>
                {users
                  .filter(user => ['MG', 'AD'].includes(user.role))
                  .map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.role === 'MG' ? 'Manager' : 'Admin'})
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Team Tags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {(editableTeam.tags || []).map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => {
                      const newTags = [...(editableTeam.tags || [])];
                      newTags.splice(index, 1);
                      setEditableTeam({ ...editableTeam, tags: newTags });
                    }}
                    size="small"
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex' }}>
                <TextField
                  label="Add Tag"
                  size="small"
                  id="edit-tag-input"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const newTag = e.target.value;
                      if (newTag && !editableTeam.tags?.includes(newTag)) {
                        setEditableTeam({
                          ...editableTeam,
                          tags: [...(editableTeam.tags || []), newTag]
                        });
                        e.target.value = '';
                      }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    const input = document.getElementById('edit-tag-input');
                    const newTag = input.value;
                    if (newTag && !editableTeam.tags?.includes(newTag)) {
                      setEditableTeam({
                        ...editableTeam,
                        tags: [...(editableTeam.tags || []), newTag]
                      });
                      input.value = '';
                    }
                  }}
                  sx={{ ml: 1 }}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleEditSubmit}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Member Dialog */}
      <Dialog open={addMemberDialogOpen} onClose={handleAddMemberClose} maxWidth="sm">
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, minWidth: 300 }}>
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUser?.id || ''}
                label="Select User"
                onChange={(e) => {
                  const userId = e.target.value;
                  const user = availableUsers.find(u => u.id === userId);
                  setSelectedUser(user);
                }}
              >
                {availableUsers.map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name} 
                    {user.designation && <span style={{ opacity: 0.8, marginLeft: '4px', marginRight: '4px' }}>
                      ({user.designation})
                    </span>}
                    <span style={{ opacity: 0.7 }}>
                      - {user.role === 'AG' ? 'Agent' : user.role === 'MG' ? 'Manager' : 'Admin'}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddMemberClose}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddMemberSubmit}
            disabled={!selectedUser}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamDetail;
